"use client";

import React, { useMemo, useState } from "react";
import RiskBadge from "@/components/RiskBadge";
import Footer from "../components/Footer";
import {
  Badge,
  GradientText,
  ScrollReveal,
  StaggerGroup,
  StaggerItem,
} from "../../components/shared";
import {
  ShieldCheckIcon,
  SearchIcon,
  WalletIcon,
  ZapIcon,
  LockIcon,
  ShieldAlertIcon,
  EyeIcon,
  GiftIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  ClockIcon,
  ActivityIcon,
  GlobeIcon,
  LinkIcon,
} from "../../components/shared/icons";

type UrlRiskCategory = "green" | "yellow" | "red";

interface UrlAuditMatch {
  id: string;
  label: string;
  count: number;
}

interface UrlAuditSignals {
  matches?: UrlAuditMatch[];
  knownGood?: {
    id: string;
    label: string;
    hostname?: string;
    hostnameMismatch?: boolean;
  };
  scoreBase?: number;
  scoreDynamicDelta?: number;
  dynamic?: { enabled?: boolean };
}

interface UrlAudit {
  _id?: string;
  url: string;
  finalUrl?: string;
  hostname?: string;
  statusCode?: number;
  contentType?: string;
  truncated?: boolean;
  riskScore: number;
  riskCategory: UrlRiskCategory;
  reasons: string[];
  signals?: UrlAuditSignals;
  createdAt?: string;
}

interface UrlAuditResponse {
  audit: UrlAudit;
}

// Icon shown next to each real heuristic match category returned by
// /api/url/audit (services/urlAudit/auditUrl.ts `matchDefs`). These six ids
// are the actual signal categories the backend computes - nothing here is
// invented the way the client mockup's "Threat Analysis" rows were (theirs
// hardcoded six rows that always read "Clean").
const MATCH_ICONS: Record<string, (p: { size?: number; style?: React.CSSProperties }) => React.ReactElement> = {
  "wallet-provider": WalletIcon,
  "tx-send": ZapIcon,
  signing: LockIcon,
  approvals: ShieldAlertIcon,
  obfuscation: EyeIcon,
  "airdrop-claim": GiftIcon,
};

const RISK_COLORS: Record<UrlRiskCategory, { stroke: string; text: string; bg: string; label: string }> = {
  green: { stroke: "#34d399", text: "#6ee7b7", bg: "rgba(16,185,129,0.12)", label: "Low Risk" },
  yellow: { stroke: "#fbbf24", text: "#fcd34d", bg: "rgba(245,158,11,0.12)", label: "Medium Risk" },
  red: { stroke: "#f87171", text: "#fca5a5", bg: "rgba(248,113,113,0.12)", label: "High Risk" },
};

function RiskCircle({ score, category }: { score: number; category: UrlRiskCategory }) {
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct / 100);
  const colors = RISK_COLORS[category] || RISK_COLORS.green;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: 132, height: 132 }}>
      <svg width={132} height={132} viewBox="0 0 132 132" className="-rotate-90">
        <circle cx={66} cy={66} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={10} />
        <circle
          cx={66}
          cy={66}
          r={r}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease-out", filter: `drop-shadow(0 0 10px ${colors.stroke}66)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold" style={{ color: "#f1f5f9" }}>{pct}</span>
        <span className="text-[10px] uppercase tracking-wide" style={{ color: colors.text }}>/ 100</span>
      </div>
    </div>
  );
}

const HISTORY_KEY = "reqrium:url-scan-history";
const HISTORY_MAX = 12;

/**
 * Normalise what the user typed into something worth sending.
 *
 * Added 2026-08-05. Previously anything non-empty was POSTed straight to
 * the API, so typing a bare domain ("example.com") produced a server-side
 * 400 and typing nonsense burned a round-trip. Most people don't type the
 * scheme, so this adds https:// when it's missing and validates locally
 * before the request goes out.
 */
function normalizeUrl(raw: string): { url: string | null; error: string | null } {
  const v = raw.trim();
  if (!v) return { url: null, error: "Enter a URL to scan." };

  const withProto = /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;

  let parsed: URL;
  try {
    parsed = new URL(withProto);
  } catch {
    return { url: null, error: "That doesn't look like a valid URL." };
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { url: null, error: "Only http:// and https:// URLs can be scanned." };
  }
  if (!parsed.hostname || !parsed.hostname.includes(".")) {
    return { url: null, error: "Enter a full domain, for example https://example.com/claim." };
  }

  return { url: parsed.toString(), error: null };
}

export default function UrlThreatScanPage() {
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UrlAuditResponse | null>(null);
  const [history, setHistory] = useState<UrlAudit[]>([]);
  const [tab, setTab] = useState<"scan" | "history">("scan");
  const [copied, setCopied] = useState(false);

  const canRun = useMemo(() => url.trim().length > 0 && !submitting, [url, submitting]);

  /**
   * Recent scans persist across reloads (added 2026-08-05). There's no
   * server-side list endpoint, so this stays local to the browser - but
   * losing your scan list on every refresh made the "Recent Scans" tab
   * almost pointless. Nothing is sent anywhere; it's the user's own record.
   */
  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(HISTORY_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) setHistory(parsed.slice(0, HISTORY_MAX) as UrlAudit[]);
    } catch {
      // corrupt or unavailable storage is not worth surfacing
    }
  }, []);

  const persistHistory = React.useCallback((next: UrlAudit[]) => {
    setHistory(next);
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      // private mode / quota - the in-memory list still works
    }
  }, []);

  function clearHistory() {
    persistHistory([]);
  }

  async function scan(rawUrl: string) {
    const { url: normalized, error: invalid } = normalizeUrl(rawUrl);
    if (!normalized) {
      setError(invalid);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setResult(null);
      setTab("scan");

      const res = await fetch("/api/url/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ url: normalized }),
      });

      if (!res.ok) {
        // The API replies with JSON `{ message }`. The old code read
        // `res.text()`, so a 400 surfaced to the user as the literal string
        // `{"message":"url is required"}` - and a 500 HTML page dumped raw
        // markup into the error box.
        const body = await res.json().catch(() => null as { message?: string } | null);
        throw new Error(body?.message || `Scan failed (${res.status})`);
      }

      const data = (await res.json()) as UrlAuditResponse;
      setResult(data);
      const withTime: UrlAudit = { ...data.audit, createdAt: data.audit.createdAt || new Date().toISOString() };
      persistHistory([withTime, ...history.filter((h) => h.url !== withTime.url)].slice(0, HISTORY_MAX));
    } catch (err) {
      setError((err as Error).message || "Scan failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function run(e: React.FormEvent) {
    e.preventDefault();
    await scan(url);
  }

  async function copySummary() {
    const a = result?.audit;
    if (!a) return;
    const text = [
      `Reqrium URL scan`,
      `URL: ${a.url}`,
      a.finalUrl && a.finalUrl !== a.url ? `Final URL: ${a.finalUrl}` : null,
      `Host: ${a.hostname || "unknown"}`,
      `Risk: ${Math.round(a.riskScore)}/100 (${RISK_COLORS[a.riskCategory]?.label || a.riskCategory})`,
      a.reasons?.length ? `Reasons:\n- ${a.reasons.join("\n- ")}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("unavailable");
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const audit = result?.audit;
  const matches = audit?.signals?.matches ?? [];
  const knownGood = audit?.signals?.knownGood;
  const dynamicRan = !!audit?.signals?.dynamic?.enabled;

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: "#0A0F1C",
        backgroundImage:
          "radial-gradient(circle at top, rgba(0,212,255,0.07), transparent 55%), radial-gradient(circle at bottom, rgba(124,58,237,0.06), transparent 55%)",
      }}
    >
      <main id="content" className="flex-1 w-full flex flex-col items-center">
        <section aria-labelledby="scan-heading" className="relative w-full px-4 pt-24 pb-4 sm:pt-28">
          <div className="mx-auto max-w-4xl">
            <ScrollReveal>
              <div
                className="relative overflow-hidden rounded-[28px] border px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  borderColor: "rgba(91,127,255,0.28)",
                  background: "linear-gradient(180deg, rgba(16,24,39,0.75), rgba(10,15,28,0.9))",
                  boxShadow:
                    "0 0 0 1px rgba(0,212,255,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(124,58,237,0.22)",
                }}
              >
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(0,212,255,0.25), transparent 70%)" }}
                  />
                  <div
                    className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(124,58,237,0.2), transparent 70%)" }}
                  />
                </div>

                <div className="relative z-10">
                  <Badge
                    icon={<ShieldCheckIcon size={14} />}
                    borderColor="rgba(0,212,255,0.4)"
                    backgroundColor="rgba(0,212,255,0.1)"
                    textColor="#67e8f9"
                  >
                    Security
                  </Badge>

                  <h1 id="scan-heading" className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                    <GradientText variant="brand">URL Threat Scan</GradientText>
                  </h1>
                  <p className="mt-3 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "var(--muted-text, #94a3b8)" }}>
                    Scan a URL for drainer-style signals (static source analysis + fingerprint checks).
                    This does not connect a real wallet.
                  </p>
                </div>

                <div className="relative z-10 mt-8" role="tablist" aria-label="Scan views">
                  <div className="pr-tabs">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === "scan"}
                      onClick={() => setTab("scan")}
                      className="pr-tab"
                    >
                      <SearchIcon size={14} />
                      Scan
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === "history"}
                      onClick={() => setTab("history")}
                      className="pr-tab"
                    >
                      <ClockIcon size={14} />
                      Recent scans
                      {history.length > 0 && (
                        <span className="ml-1 rounded-full bg-white/12 px-1.5 py-0.5 text-[10px] font-bold">
                          {history.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="relative w-full px-4 pb-16">
          <div className="mx-auto max-w-4xl">
            {tab === "scan" ? (
              <>
                <div className="pr-panel">
                  <label htmlFor="scan-url" className="pr-label">Scan a URL</label>
                  <form onSubmit={run} className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div
                      className="flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-2.5 rounded-xl border px-4 py-2.5"
                      style={{ borderColor: "rgba(91,127,255,0.24)", background: "rgba(3,8,20,0.55)" }}
                    >
                      <LinkIcon size={16} className="shrink-0" style={{ color: "#94a3b8" }} />
                      <input
                        id="scan-url"
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                          if (error) setError(null);
                        }}
                        placeholder="example.com/claim"
                        className="min-w-0 flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="url"
                      />
                    </div>
                    <div className="pr-actions">
                      <button type="submit" disabled={!canRun} className="pr-btn pr-btn-primary pr-btn-sm">
                        {submitting ? "Scanning…" : "Scan URL"}
                      </button>
                    </div>
                  </form>
                  <p className="pr-hint mt-3">
                    No scheme needed — <span className="font-mono">example.com</span> is treated as{" "}
                    <span className="font-mono">https://example.com</span>. Nothing is opened in your browser and no
                    wallet is connected.
                  </p>

                  {error ? (
                    <div
                      className="mt-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm"
                      style={{ borderColor: "rgba(248,113,113,0.35)", background: "rgba(127,29,29,0.18)" }}
                      role="alert"
                    >
                      <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" style={{ color: "#fca5a5" }} />
                      <div className="min-w-0">
                        <div className="mb-0.5 font-semibold" style={{ color: "#fecaca" }}>Couldn&rsquo;t scan that</div>
                        <div className="break-words" style={{ color: "#fecaca" }}>{error}</div>
                      </div>
                    </div>
                  ) : null}
                </div>

                {audit ? (
                  <ScrollReveal>
                    <div
                      className="mt-6 rounded-3xl border overflow-hidden"
                      style={{ borderColor: "rgba(91,127,255,0.2)", background: "var(--glass-bg, rgba(16,24,39,0.55))", backdropFilter: "blur(18px)" }}
                    >
                      <div
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-5 py-4 text-xs sm:px-6"
                        style={{ borderColor: "rgba(91,127,255,0.16)" }}
                      >
                        <span style={{ color: "var(--muted-text, #94a3b8)" }}>Result</span>
                        <span className="inline-flex min-w-0 items-center gap-1.5" style={{ color: "#67e8f9" }}>
                          <GlobeIcon size={13} className="shrink-0" />
                          <span className="truncate">{audit.hostname || "(unknown host)"}</span>
                        </span>
                        {/* Added: an analyst's first move after a scan is to
                            pass the finding on. */}
                        <button type="button" onClick={copySummary} className="pr-btn pr-btn-ghost pr-btn-sm ml-auto">
                          {copied ? <CheckCircleIcon size={13} /> : null}
                          <span>{copied ? "Copied" : "Copy summary"}</span>
                        </button>
                      </div>

                      <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-6">
                        <div className="flex flex-col items-center gap-3 shrink-0">
                          <RiskCircle score={audit.riskScore} category={audit.riskCategory} />
                          <RiskBadge score={audit.riskScore} category={audit.riskCategory} />
                        </div>

                        <div className="flex-1 min-w-0 space-y-4">
                          <div>
                            <div className="text-xs uppercase tracking-[0.16em]" style={{ color: "var(--muted-text, #94a3b8)" }}>URL</div>
                            <div className="mt-1 text-sm break-all" style={{ color: "#f1f5f9" }}>{audit.url}</div>
                            {audit.finalUrl && audit.finalUrl !== audit.url ? (
                              <div className="mt-1.5 text-xs break-all" style={{ color: "var(--muted-text, #94a3b8)" }}>
                                Final: <span className="font-mono">{audit.finalUrl}</span>
                              </div>
                            ) : null}
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                            <div className="rounded-xl border px-3 py-2" style={{ borderColor: "rgba(91,127,255,0.16)", background: "rgba(8,12,22,0.4)" }}>
                              <div style={{ color: "var(--muted-text, #94a3b8)" }}>Status</div>
                              <div className="mt-0.5 font-medium" style={{ color: "#f1f5f9" }}>{typeof audit.statusCode === "number" ? audit.statusCode : "n/a"}</div>
                            </div>
                            <div className="rounded-xl border px-3 py-2" style={{ borderColor: "rgba(91,127,255,0.16)", background: "rgba(8,12,22,0.4)" }}>
                              <div style={{ color: "var(--muted-text, #94a3b8)" }}>Content Type</div>
                              <div className="mt-0.5 font-medium truncate" style={{ color: "#f1f5f9" }}>{audit.contentType || "n/a"}</div>
                            </div>
                            <div className="rounded-xl border px-3 py-2" style={{ borderColor: "rgba(91,127,255,0.16)", background: "rgba(8,12,22,0.4)" }}>
                              <div style={{ color: "var(--muted-text, #94a3b8)" }}>Truncated</div>
                              <div className="mt-0.5 font-medium" style={{ color: "#f1f5f9" }}>{audit.truncated ? "Yes" : "No"}</div>
                            </div>
                          </div>

                          {knownGood ? (
                            <div
                              className="flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs"
                              style={{
                                borderColor: knownGood.hostnameMismatch ? "rgba(248,113,113,0.35)" : "rgba(34,197,94,0.3)",
                                background: knownGood.hostnameMismatch ? "rgba(127,29,29,0.18)" : "rgba(16,185,129,0.1)",
                              }}
                            >
                              {knownGood.hostnameMismatch ? (
                                <AlertTriangleIcon size={15} className="mt-0.5 shrink-0" style={{ color: "#fca5a5" }} />
                              ) : (
                                <CheckCircleIcon size={15} className="mt-0.5 shrink-0" style={{ color: "#4ade80" }} />
                              )}
                              <span style={{ color: knownGood.hostnameMismatch ? "#fecaca" : "#bbf7d0" }}>
                                {knownGood.hostnameMismatch
                                  ? `Content matches known-good fingerprint "${knownGood.label}" but the hostname differs - possible clone.`
                                  : `Content matches known-good fingerprint "${knownGood.label}".`}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4">
                        <div>
                          <div className="text-xs uppercase tracking-[0.16em] mb-2.5 flex items-center gap-1.5" style={{ color: "var(--muted-text, #94a3b8)" }}>
                            <ActivityIcon size={13} />
                            Heuristic Signal Breakdown
                          </div>
                          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {matches.map((m) => {
                              const Icon = MATCH_ICONS[m.id] || ActivityIcon;
                              const detected = m.count > 0;
                              return (
                                <StaggerItem key={m.id}>
                                  <div
                                    className="flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5"
                                    style={{
                                      borderColor: detected ? "rgba(248,113,113,0.3)" : "rgba(34,197,94,0.2)",
                                      background: detected ? "rgba(127,29,29,0.12)" : "rgba(8,12,22,0.4)",
                                    }}
                                  >
                                    <span className="inline-flex items-center gap-2 text-sm" style={{ color: "#f1f5f9" }}>
                                      <Icon size={15} style={{ color: detected ? "#fca5a5" : "#94a3b8" }} />
                                      {m.label}
                                    </span>
                                    <span
                                      className="text-xs font-semibold shrink-0"
                                      style={{ color: detected ? "#fca5a5" : "#4ade80" }}
                                    >
                                      {detected ? `Detected ×${m.count}` : "Clean"}
                                    </span>
                                  </div>
                                </StaggerItem>
                              );
                            })}
                          </StaggerGroup>
                        </div>

                        <div>
                          <div className="text-xs uppercase tracking-[0.16em] mb-2" style={{ color: "var(--muted-text, #94a3b8)" }}>
                            Reasons
                          </div>
                          {audit.reasons && audit.reasons.length > 0 ? (
                            <ul className="space-y-1.5">
                              {audit.reasons.map((r, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: "#e2e8f0" }}>
                                  <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ background: "#67e8f9" }} />
                                  {r}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-sm" style={{ color: "var(--muted-text, #94a3b8)" }}>No reasons recorded.</div>
                          )}
                        </div>

                        {dynamicRan && typeof audit.signals?.scoreBase === "number" ? (
                          <div className="text-[11px]" style={{ color: "var(--muted-text, #94a3b8)" }}>
                            Static analysis score: {audit.signals.scoreBase} · Dynamic sandbox adjustment:{" "}
                            {(audit.signals.scoreDynamicDelta ?? 0) >= 0 ? "+" : ""}
                            {audit.signals.scoreDynamicDelta ?? 0}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </ScrollReveal>
                ) : null}
              </>
            ) : (
              <div className="pr-panel">
                <div className="pr-panel-head">
                  <span className="pr-panel-icon">
                    <ClockIcon size={16} />
                  </span>
                  <span className="pr-panel-title">Recent scans</span>
                  {history.length > 0 && (
                    <button type="button" onClick={clearHistory} className="pr-btn pr-btn-ghost pr-btn-sm ml-auto">
                      Clear history
                    </button>
                  )}
                  <p className="pr-panel-sub">
                    Kept in this browser only — never uploaded. Select a scan to run it again.
                  </p>
                </div>

                {history.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/12 px-6 py-14 text-center">
                    <ClockIcon size={22} style={{ color: "#94a3b8" }} />
                    <p className="pr-hint max-w-sm">No scans yet. Run one and it&rsquo;ll be saved here.</p>
                  </div>
                ) : (
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {history.map((h, i) => {
                      const colors = RISK_COLORS[h.riskCategory] || RISK_COLORS.green;
                      const when = h.createdAt ? new Date(h.createdAt) : null;
                      return (
                        <li key={(h._id || h.url) + i}>
                          {/* Was a static div. Re-running a previously scanned
                              URL is the single most common follow-up action,
                              and there was no way to do it without retyping. */}
                          <button
                            type="button"
                            onClick={() => {
                              setUrl(h.url);
                              void scan(h.url);
                            }}
                            disabled={submitting}
                            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/[0.09] bg-white/[0.025] px-4 py-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-cyan-400/35 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70 disabled:opacity-60"
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-semibold text-slate-100">
                                {h.hostname || h.url}
                              </span>
                              <span className="block truncate text-xs" style={{ color: "var(--muted-text, #94a3b8)" }}>
                                {when && !isNaN(when.getTime()) ? when.toLocaleString() : h.url}
                              </span>
                            </span>
                            <span
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                              style={{ background: colors.bg, color: colors.text }}
                            >
                              {Math.round(h.riskScore)} · {colors.label}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-8 flex items-start gap-2 text-xs" style={{ color: "var(--muted-text, #64748b)" }}>
              <ShieldCheckIcon size={14} className="mt-0.5 shrink-0" />
              This scanner performs server-side fetching with guardrails to reduce SSRF risk. Optional dynamic
              sandboxing (Playwright) is environment-gated and skipped on Vercel.
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
